import { useNavigate } from 'react-router-dom';
import { FC, PropsWithChildren, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';

import { AuthApi, UserAccess } from 'api';
import appRoutes from 'app.routes';
import { useAuth, useMindworkingUrl } from 'modules/auth/hooks';
import { useRefreshToken } from 'hooks/general/useRefreshToken';
import { useLocalization } from 'localization';

interface PageProps extends PropsWithChildren {
  guard?: boolean | 'admin';
  titleKey: string;
}

const AuthLayout: FC<PageProps> = ({ guard, titleKey, children }) => {
  const userAuth = useAuth();
  const { t, locale } = useLocalization();
  const userIsAdmin =
    !userAuth.user?.isInternalUser ||
    !!userAuth.user?.branches.some(branch => branch.access === UserAccess.Admin) ||
    userAuth.userIsLoading;
  const isGuarded = (guard && !AuthApi.isAuthenticated()) || (guard == 'admin' && !userIsAdmin);
  const [redirectChecked, setRedirectChecked] = useState(false);
  const { isMindworking } = useMindworkingUrl();
  const navigate = useNavigate();

  //converted iframe jsx to a custom hook
  useRefreshToken();

  const handleAutomaticallyNavigation = () => {
    if (guard == 'admin') {
      navigate(appRoutes.dashboard(), { replace: true });
      return;
    }

    userAuth.persistRedirectUrl();

    if (isMindworking) return;

    navigate(appRoutes.login(), { replace: true });
  };

  useEffect(() => {
    if (document.location.hash.includes('#/interview')) {
      const params = document.location.hash.split('?')[1] || '';
      window.location.href = `${window.location.origin}/interview?${params}`;
      return;
    }
    if (document.location.hash.includes('#/adduser')) {
      const params = document.location.hash.split('?')[1] || '';
      window.location.href = `${window.location.origin}/accept-invite?${params}`;
      return;
    }
    if (document.location.hash.includes('#/presentation')) {
      const params = document.location.hash.split('?')[1];
      if (params) {
        const query = new URLSearchParams(params);
        const { caseId, clientId, filter } = {
          caseId: query.get('r'),
          clientId: query.get('c'),
          filter: query.get('v'),
        };
        window.location.href = `${window.location.origin}/presentation?${caseId ? `caseId=${caseId}&` : ''}${
          clientId ? `clientId=${clientId}&` : ''
        }${filter ? `filter=${filter}` : ''}`;
        return;
      }
    }
    if (document.location.hash.includes('#/booking')) {
      const params = document.location.hash.split('?')[1] || '';
      window.location.href = `${window.location.origin}/booking?${params}`;
      return;
    }
    if (document.location.hash.includes('#/resetpassword')) {
      const params = document.location.hash.split('?')[1] || '';
      window.location.href = `${window.location.origin}/reset-password?${params}`;
      return;
    }
    setRedirectChecked(true);
  }, []);

  useEffect(() => {
    if (isGuarded) {
      handleAutomaticallyNavigation();
    }
  }, [isGuarded]);

  if (isGuarded || !redirectChecked) {
    return null;
  }

  return (
    <>
      <Helmet key={titleKey}>
        <title>{t(titleKey)}</title>
      </Helmet>
      {children}
    </>
  );
};

export default AuthLayout;

//copy and keep logic from src\modules\common\Page.tsx
